import {
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridRenderCellParams,
    GridRowSelectionModel,
} from '@mui/x-data-grid-pro'
import {
    MutableRefObject,
    useCallback,
    useMemo,
    useState,
    useRef, useEffect,
} from 'react'

import {
    ExtendedGridApiPro,
} from 'app/shared-components/SkyBaseDataGrid'
import {
    INIT_SELECTION_MODEL,
} from 'app/shared-components/SkyBaseDataGrid/constants'
import {
    SelectionType,
} from '../SkyNetDataGrid'

import SelectionCell from '../components/SelectionCell'

export default function useRowSelection({
    selectionType,
    apiRef,
    onSelectedRow,
    disabled,
    selectedRows,
    selectionModelType = 'include',
}: {
    selectionType?: SelectionType,
    apiRef: MutableRefObject<ExtendedGridApiPro>,
    onSelectedRow?: (selectedIds: Set<string | number>) => void,
    disabled?: boolean,
    selectedRows?: (string | number)[],
    selectionModelType?: 'include' | 'exclude',
}) {
    const selectedRowsInit = useRef(selectedRows)
    const disabledRef = useRef(disabled)
    const [
        selectionModel,
        setSelectionModel,
    ] = useState<GridRowSelectionModel>(() => {
        return {
            ...INIT_SELECTION_MODEL,
            type: selectionModelType,
            ids: new Set(selectedRowsInit.current),
        }
    })

    const handleRadioSelection = useCallback((id: number | string) => {
        const newSelection = {
            ...INIT_SELECTION_MODEL,
            type: selectionModelType,
            ids: new Set([id]),
        }

        apiRef.current.setRowSelectionModel(newSelection, 'singleRowSelection')
    }, [
        apiRef,
        selectionModelType,
    ])

    const handleCheckboxSelection = useCallback((id: number | string) => {
        const currentSelection = apiRef.current?.getSelectedRows()

        const newIds = new Set(currentSelection.keys())

        if (newIds.has(id)) {
            newIds.delete(id)
        } else {
            newIds.add(id)
        }

        const newSelection = {
            ...INIT_SELECTION_MODEL,
            type: selectionModelType,
            ids: newIds,
        }

        apiRef.current.setRowSelectionModel(newSelection, 'multipleRowsSelection')
    }, [
        apiRef,
        selectionModelType,
    ])

    const onRowSelectionModelChange = useCallback((newSelectionModel: GridRowSelectionModel) => {
        setSelectionModel(newSelectionModel)
        onSelectedRow?.(newSelectionModel.ids)
    }, [onSelectedRow])

    useEffect(() => {
        // safely pass disabled state to SelectionCell without re-rendering of columns
        apiRef.current?.publishEvent?.('disabledStateChange' as any, disabled)
        disabledRef.current = disabled
    }, [
        apiRef,
        disabled,
    ])

    const customRowSelectionColumn = useMemo(() => {
        return {
            ...GRID_CHECKBOX_SELECTION_COL_DEF,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <SelectionCell
                        rowId={params.id}
                        selectionType={selectionType}
                        onRadioChange={handleRadioSelection}
                        onCheckboxChange={handleCheckboxSelection}
                        apiRef={apiRef}
                        initiallyDisabled={disabledRef.current}
                    />
                )
            },
        }
    }, [
        selectionType,
        handleRadioSelection,
        handleCheckboxSelection,
        apiRef,
    ])

    return {
        selectionModel,
        customRowSelectionColumn,
        onRowSelectionModelChange,
        disableRowSelectionOnClick: !!selectionType,
    }
}
